import { useEffect, useState } from 'react'
import { ChevronDown, ChevronUp, SlidersHorizontal, Loader2, Zap, Check, AlertTriangle } from 'lucide-react'
import { proxyUrl, buildHeaders } from '@features/dashboards/testing-ground/utils'
import type { Creds, LogEntry } from '@features/dashboards/testing-ground/types'
import { DEFAULT_AML_CONFIG } from '../data'
import type { AmlScreeningConfig } from '../types'
import { useAiSeed } from '../aiConfigure'
import type { AiSeed } from '../aiConfigure'

interface AmlConfigPanelProps {
  creds: Creds
  hasCreds: boolean
  addLog: (e: Omit<LogEntry, 'id' | 'ts'>) => void
  entityId: string | null
  onConfigChange: (config: AmlScreeningConfig) => void
  /** AI Configure from Scope — applied once the rep accepts the suggestion.
   * Ongoing monitoring is deliberately left out of the seed: it's a real,
   * separate API call against a live entity, not a config field to pre-fill. */
  aiSeed?: AiSeed<Pick<AmlScreeningConfig, 'sanctions' | 'pep' | 'adverseMedia'>> | null
}

function Toggle({ checked, onChange, disabled }: { checked: boolean; onChange: () => void; disabled?: boolean }) {
  return (
    <label className="relative inline-flex items-center cursor-pointer shrink-0" style={disabled ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}>
      <input type="checkbox" checked={checked} onChange={onChange} disabled={disabled} className="sr-only peer" />
      <div className="w-8 h-[18px] rounded-full transition-colors" style={{ background: checked ? '#6366f1' : '#e5e7eb' }} />
      <div className="absolute left-[3px] top-[3px] w-3 h-3 rounded-full bg-white transition-transform" style={{ transform: checked ? 'translateX(14px)' : 'translateX(0)' }} />
    </label>
  )
}

export function AmlConfigPanel({ creds, hasCreds, addLog, entityId, onConfigChange, aiSeed }: AmlConfigPanelProps) {
  const [open, setOpen] = useState(true)
  const [config, setConfig] = useState<AmlScreeningConfig>(DEFAULT_AML_CONFIG)
  const [monitorLoading, setMonitorLoading] = useState(false)
  const [monitorError, setMonitorError] = useState<string | null>(null)

  useAiSeed(aiSeed, (suggestion) => setConfig((prev) => ({ ...prev, ...suggestion })))

  useEffect(() => {
    onConfigChange(config)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  function toggleInformational(key: 'sanctions' | 'pep' | 'adverseMedia') {
    setConfig((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  async function toggleMonitoring() {
    if (!entityId) return
    const next = !config.ongoingMonitoring
    setMonitorLoading(true)
    setMonitorError(null)
    const url = `${creds.apiBase}/individuals/${entityId}/monitor`
    const start = Date.now()
    try {
      const res = await fetch(proxyUrl(`/individuals/${entityId}/monitor`), {
        method: 'PATCH',
        headers: buildHeaders(creds, true),
        body: JSON.stringify({ monitor: next }),
      })
      const durationMs = Date.now() - start
      let data: unknown
      try { data = await res.json() } catch { data = {} }
      addLog({ method: 'PATCH', url, status: res.status, durationMs, requestBody: { monitor: next }, responseBody: data, annotation: 'Toggle ongoing monitoring for this entity' })
      if (res.ok) {
        setConfig((prev) => ({ ...prev, ongoingMonitoring: next }))
      } else {
        setMonitorError((data as { error?: string })?.error ?? `Request failed (${res.status})`)
      }
    } catch {
      setMonitorError('Network error')
    } finally {
      setMonitorLoading(false)
    }
  }

  return (
    <div className="rounded-[14px] overflow-hidden" style={{ border: '1px solid #e5e7eb', background: '#ffffff' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-2.5 transition-colors hover:bg-[#ffffff]"
        style={{ background: '#f8fafc' }}
      >
        <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748b]">
          <SlidersHorizontal size={12} className="text-[#4f46e5]" />
          AML Screening Configuration
        </span>
        {open ? <ChevronUp size={12} className="text-[#64748b]" /> : <ChevronDown size={12} className="text-[#64748b]" />}
      </button>

      {open && (
        <div className="p-4 space-y-3">
          <p className="text-[10px] text-[#475569]">
            Sanctions, PEP and adverse media screening run together as part of the workflow&apos;s AML step — the toggles below are a reference for what&apos;s screened, not independent live switches. Ongoing monitoring is the exception: it&apos;s a real, separate call.
          </p>

          {[
            { key: 'sanctions' as const, label: 'Sanctions Screening', description: 'Screens against global sanctions lists (OFAC, UN, EU and more).' },
            { key: 'pep' as const, label: 'PEP Screening', description: 'Flags politically-exposed persons and their known associates.' },
            { key: 'adverseMedia' as const, label: 'Adverse Media', description: 'Searches news and public records for negative coverage.' },
          ].map((item) => (
            <div key={item.key} className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-[#1e293b]">{item.label}</p>
                <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">{item.description}</p>
              </div>
              <Toggle checked={config[item.key]} onChange={() => toggleInformational(item.key)} />
            </div>
          ))}

          <div className="flex items-center gap-3 px-3 py-2.5 rounded-[9px]" style={{ background: 'rgba(52,211,153,.05)', border: '1px solid rgba(52,211,153,.25)' }}>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-[#1e293b] flex items-center gap-1.5">
                Ongoing Monitoring
                <span className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'rgba(52,211,153,.15)', color: '#15803d' }}>Live</span>
              </p>
              <p className="text-[10px] text-[#64748b] mt-0.5 leading-snug">Re-screens this entity automatically as watchlists change. This toggle makes a real call to FrankieOne.</p>
            </div>
            {monitorLoading ? <Loader2 size={14} className="animate-spin text-[#64748b] shrink-0" /> : (
              <Toggle checked={config.ongoingMonitoring} onChange={() => void toggleMonitoring()} disabled={!entityId || !hasCreds} />
            )}
          </div>
          {!entityId && <p className="text-[10px] text-amber-700">Run IDV or KYC first to create a test entity before toggling monitoring.</p>}
          {monitorError && <p className="text-[10px] text-red-700 flex items-center gap-1"><AlertTriangle size={10} /> {monitorError}</p>}
          {entityId && !monitorError && (
            <p className="text-[10px] text-emerald-700 flex items-center gap-1"><Zap size={10} /> {config.ongoingMonitoring ? <>Monitoring is <Check size={10} className="inline" /> enabled for this entity</> : 'Monitoring is currently off for this entity'}</p>
          )}
        </div>
      )}
    </div>
  )
}
